/**
 * مؤشّرات أداء فريق التنفيذ.
 *
 * فريقُ المبيعات يُقاس بالمال: هدفٌ ومحقَّقٌ وعمولة. وفريقُ التنفيذ لا يبيع،
 * فقياسُه بمسطرة المبيعات لا معنى له — والسؤالُ عنه مختلف: هل يُسلّم في
 * موعده، وهل يُقبل ما يُسلّمه من أوّل مرّة، وهل يرضى عنه العميل؟
 *
 * ## ثلاثةُ بنودٍ لا سبعة
 *
 * كلُّ بندٍ هنا نجا من سؤالٍ واحد: «هل يغيّر المديرُ قرارًا بسببه؟». وما لم
 * ينجُ سقط ومعه سببُ سقوطه مكتوبًا (انظر `REJECTED_KPIS`) — فسبعةُ بنودٍ
 * نصفُها زينةٌ تُعيد توزيع وزنها في صمت، وثلاثةٌ صلبةٌ تُقرأ ويُحتكم إليها.
 *
 * ## والفعليُّ محسوبٌ لا مكتوب
 *
 * ما يُضبط باليد اثنان: مرابطُ الأهداف وأوزانُ البنود. أمّا الأرقامُ
 * فتُحسب من المهامّ والمراجعات لحظةَ القراءة — كما يُحسب استهلاكُ الباقة
 * وتقدّمُ الهدف في هذه الوحدة. ونسبةٌ تُكتب باليد تنتهي رقمًا يجمّل
 * الاجتماعات.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   مراجعة التسليم — شهادةٌ على عملٍ بعينه لا خلاصةٌ عن شخص
   ========================================================================== */

export const REVIEW_VERDICTS = ['APPROVED', 'REWORK'] as const;
export type ReviewVerdict = (typeof REVIEW_VERDICTS)[number];

export const REVIEW_VERDICT_DEFS: readonly Labelled<ReviewVerdict>[] = [
  { key: 'APPROVED', label: 'مقبول', kind: 'success' },
  { key: 'REWORK', label: 'يُردّ للتعديل', kind: 'warning' },
] as const;

export const REVIEW_VERDICT_MAP = Object.fromEntries(
  REVIEW_VERDICT_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ReviewVerdict, Labelled<ReviewVerdict>>>;

/**
 * سببُ الردّ — وهو أداةُ الإنصاف كلُّها.
 *
 * ما رُدَّ لتغيُّر طلب العميل أو لبريفٍ ناقصٍ ليس عيبَ المنفّذ. والسببُ
 * يكتبه المراجعُ لا المنفّذ، ومن كتالوجٍ مغلقٍ لا نصٍّ حرّ: نصٌّ حرٌّ يجعل
 * كلَّ ردٍّ قصّةً لا تُجمع مع أختها.
 */
export const REWORK_CAUSES = ['EXECUTION', 'CLIENT_SCOPE', 'BRIEF_GAP', 'INTERNAL_QA'] as const;
export type ReworkCause = (typeof REWORK_CAUSES)[number];

export interface ReworkCauseDef extends Labelled<ReworkCause> {
  /** هل يُحتسب على المنفّذ. */
  onExecutor: boolean;
  hint: string;
}

export const REWORK_CAUSE_DEFS: readonly ReworkCauseDef[] = [
  {
    key: 'EXECUTION',
    label: 'عيبٌ في التنفيذ',
    kind: 'danger',
    onExecutor: true,
    hint: 'العملُ نفسُه ناقصٌ أو مخالفٌ للمتّفق عليه — وهذا وحدَه يُحتسب على المنفّذ.',
  },
  {
    key: 'CLIENT_SCOPE',
    label: 'تغيّر طلبُ العميل',
    kind: 'info',
    onExecutor: false,
    hint: 'العميلُ غيّر رأيَه بعد التسليم — يُسجَّل ولا يُحتسب على أحد.',
  },
  {
    key: 'BRIEF_GAP',
    label: 'نقصٌ في البريف',
    kind: 'warning',
    onExecutor: false,
    hint: 'لم يكن المطلوبُ واضحًا أصلًا — يُحتسب على من كتب البريف لا على من نفّذ.',
  },
  {
    key: 'INTERNAL_QA',
    label: 'صقلٌ داخليّ',
    kind: 'neutral',
    onExecutor: false,
    hint: 'تحسينٌ اختياريٌّ لرفع المستوى — لا عيبَ فيه.',
  },
] as const;

export const REWORK_CAUSE_MAP = Object.fromEntries(
  REWORK_CAUSE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<ReworkCause, ReworkCauseDef>>;

/* ==========================================================================
   البنود الثلاثة
   ========================================================================== */

export const AGENCY_KPIS = ['ON_TIME', 'REWORK', 'CLIENT_RATING'] as const;
export type AgencyKpiKey = (typeof AGENCY_KPIS)[number];

export interface AgencyKpiDef {
  key: AgencyKpiKey;
  label: string;
  description: string;
  /** ما يُقاس به: نسبةٌ مئوية أو نجومٌ من خمس. */
  unit: 'PERCENT' | 'STARS';
  direction: 'HIGHER_BETTER' | 'LOWER_BETTER';
  weight: number;
  /**
   * المرابطُ الثلاثة بوحدة البند: أرضيةٌ (صفر نقطة)، وهدفٌ (ثمانون)،
   * وطموحٌ (مئة). والهدفُ عند ثمانين عمدًا: بلوغُ كلّ الأهداف بالضبط
   * «جيّدٌ جدًّا» لا كمال، فيبقى العشرون مجالًا حقيقيًّا.
   */
  floor: number;
  target: number;
  stretch: number;
  /** أقلُّ عدد فرصٍ يُحتسب معه البند — دونه لا رقم. */
  nMin: number;
  /** كيف يُلعَب، وبمَ حُرس. */
  gaming: string;
  guard: string;
}

export const AGENCY_KPI_DEFS: readonly AgencyKpiDef[] = [
  {
    key: 'ON_TIME',
    label: 'التسليم في موعده',
    description:
      'حصّةُ ما سُلّم في موعده الأوّل من تسليماته المؤرَّخة — يُقاس على الموعد المجمَّد لا على آخر موعدٍ حُرّك.',
    unit: 'PERCENT',
    direction: 'HIGHER_BETTER',
    weight: 40,
    floor: 60,
    target: 85,
    stretch: 96,
    nMin: 8,
    gaming:
      'أن يحرّك الموعدَ بنفسه قُبيل استحقاقه، أو يترك الصعبَ بلا موعدٍ أصلًا فيسقط من المقام، أو يشظّي تسليمًا خطِرًا إلى بطاقاتٍ سهلة.',
    guard:
      'الموعدُ الأوّل يُجمَّد عند أوّل تأريخٍ ولا يُكتب ثانيةً، والقياسُ عليه وحدَه. وما بلا موعدٍ يُعدّ ويُعرض صراحةً. والعدُّ على المهامّ الأمّهات وحدَها فالتشظيةُ لا تُنتج التزامات.',
  },
  {
    key: 'REWORK',
    label: 'الردّ للتعديل',
    description:
      'حصّةُ ما رُدَّ من تسليماته لعيبٍ في التنفيذ إلى ما رُوجع منها — وما رُدَّ لتغيّر طلب العميل أو لنقص البريف يُعرض ولا يُحتسب.',
    unit: 'PERCENT',
    direction: 'LOWER_BETTER',
    weight: 35,
    floor: 30,
    target: 10,
    stretch: 2,
    nMin: 8,
    gaming:
      'أن يبرّئ المراجعُ زميلَه فيكتب سببًا غير «عيب التنفيذ»، أو أن يُراجَع السهلُ ويُترك الصعب، أو ألّا يشحن المنفّذُ إلا الآمن فلا يُردّ عليه شيء.',
    guard:
      'السببُ يكتبه المراجعُ من كتالوجٍ مغلق، ولا يراجع أحدٌ عملَ نفسه ولا ما أنشأه. والجولةُ الأولى وحدَها تُحتسب فالنسخةُ المصقولةُ بعد الردّ لا تُكافأ. وحصّةُ ما رُوجع من إجمالِ ما أُنجز معروضةٌ بجواره: مراجعتان من ثلاثين لا تُقرآن كعشرين من ثلاثين.',
  },
  {
    key: 'CLIENT_RATING',
    label: 'رأي العميل',
    description:
      'وسيطُ نجوم العميل على التسليمات التي كان هو المُسنَدَ إليه فيها — صوتُ العميل على عمله هو لا على المشروع كلِّه.',
    unit: 'STARS',
    direction: 'HIGHER_BETTER',
    weight: 25,
    floor: 3.4,
    target: 4.3,
    stretch: 4.8,
    nMin: 5,
    gaming: 'أن يطلب التقييمَ بنفسه ممّن يعرف أنّه راضٍ، أو ألّا يطلبه إلا بعد مكالمةِ إرضاء.',
    guard: 'طالبُ التقييم لا يكون هو المنفّذ، والتقييمُ منسوبٌ إلى تسليمٍ بعينه لا إلى المشروع.',
  },
] as const;

export const AGENCY_KPI_MAP = Object.fromEntries(
  AGENCY_KPI_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<AgencyKpiKey, AgencyKpiDef>>;

/**
 * بنودٌ رُفضت — تُكتب لئلّا تُقترح ثانيةً.
 *
 * كلُّ واحدٍ منها بدا معقولًا حتى سُئل: كيف يُلعَب، وهل يُحسب أصلًا؟
 */
export const REJECTED_KPIS: readonly { label: string; why: string }[] = [
  {
    label: 'عددُ المهامّ المنجَزة في اليوم المتاح',
    why: 'يكافئ التشظية: من قسّم تسليمًا إلى اثنتَي عشرةَ بطاقةً ربح اثنَي عشرَ إنجازًا. ومقامُه من جدول الحضور غيرُ صالح — صفوفُه لا تُكتب إلّا حين يحفظ موظّفُ الموارد البشرية ورقةَ اليوم، فأسبوعٌ لم يُسجَّل يرفع الدرجة.',
  },
  {
    label: 'وحداتُ الخدمة المسلَّمة',
    why: 'حقلُ الوحدات يحرّره المنفّذُ نفسُه من بطاقة المهمّة — فالمقيسُ يكتب بسطَه بيده.',
  },
  {
    label: 'زمنُ البدء بعد الإسناد',
    why: 'يُطفَأ عدّادُه بتعليقٍ من أربعين حرفًا لحظةَ وصول البطاقة. وبوزنٍ صغيرٍ يصنع ضجيجًا لا إشارة.',
  },
  {
    label: 'عددُ مرّات إعادة الفتح',
    why: 'إعادةُ الفتح محروسةٌ بكلمة سرّ الحماية ونادرةٌ بنيويًّا — فقياسُها يقيس من يملك كلمةَ السرّ لا من يعيد العمل.',
  },
  {
    label: 'دقائقُ التأخّر في الحضور',
    why: 'حضورٌ لا يقيس قيمةً سُلّمت. ويدخل في «الأيام المتاحة» لتقسيط حدّ العيّنة، ولا يدخل الدرجة.',
  },
  {
    label: 'أيُّ رقمٍ من طبقة المراقبة',
    why: 'اللقطاتُ ونبضُ النشاط للأمن ومراجعة الحوادث — وقياسُها يقيس الامتثالَ للمراقِب لا العملَ المسلَّم.',
  },
] as const;

/* ==========================================================================
   الحساب — دوالُّ خالصةٌ تُختبر وحدها
   ========================================================================== */

/**
 * تنعيمُ لابلاس.
 *
 * واحدةٌ من واحدةٍ ليست مئةً بالمئة، ولا صفرٌ من واحدةٍ صفرٌ — رجلٌ سلّم
 * تسليمًا واحدًا في موعده ليس أفضلَ من سلّم تسعةً من عشرة.
 */
export function smoothRatio(hits: number, total: number): number | null {
  if (total <= 0) return null;
  return ((hits + 1) / (total + 2)) * 100;
}

/**
 * تسويةُ قيمةٍ إلى نقاطٍ من مئة بمرابطَ ثلاثة.
 *
 * الهدفُ عند ثمانين لا مئة: من بلغ كلَّ أهدافه بالضبط «جيّدٌ جدًّا» لا
 * كامل، فيبقى فوقه مجالٌ حقيقيٌّ يُسعى إليه.
 */
export function scoreKpi(value: number, def: AgencyKpiDef): number {
  const clamp = (n: number) => Math.max(0, Math.min(1, n));
  const { floor: f, target: t, stretch: s } = def;
  const base = clamp((value - f) / (t - f));
  const extra = clamp((value - t) / (s - t));
  return Math.round(80 * base + 20 * extra);
}

export const KPI_GRADES = ['STRONG', 'GOOD', 'WATCH', 'WEAK'] as const;
export type KpiGrade = (typeof KPI_GRADES)[number];

export interface KpiGradeDef extends Labelled<KpiGrade> {
  from: number;
  hint: string;
}

export const KPI_GRADE_DEFS: readonly KpiGradeDef[] = [
  { key: 'STRONG', label: 'متميّز', kind: 'success', from: 85, hint: 'فوق الهدف في أغلب البنود.' },
  { key: 'GOOD', label: 'جيّد', kind: 'brand', from: 70, hint: 'يبلغ أهدافه أو يقاربها.' },
  { key: 'WATCH', label: 'يحتاج متابعة', kind: 'warning', from: 50, hint: 'بندٌ أو أكثرُ دون الهدف بوضوح.' },
  { key: 'WEAK', label: 'متعثّر', kind: 'danger', from: 0, hint: 'جلسةُ عملٍ مع مديره، لا رسالةُ نظام.' },
] as const;

export function gradeOf(score: number): KpiGradeDef {
  return KPI_GRADE_DEFS.find((g) => score >= g.from) ?? KPI_GRADE_DEFS[KPI_GRADE_DEFS.length - 1]!;
}

/** أدنى تغطيةٍ من الأوزان يُعلَن معها رقمٌ عامّ — ودونها «لا قياس». */
export const MIN_COVERAGE = 0.6;
/** أقلُّ عدد بنودٍ محتسَبةٍ لإعلان رقمٍ عامّ. */
export const MIN_KPIS = 2;
/** نافذةُ القياس الافتراضية بالأيام. */
export const KPI_WINDOW_DAYS = 90;

/* ==========================================================================
   الشكل المعاد
   ========================================================================== */

export interface KpiValue {
  key: AgencyKpiKey;
  /** القيمةُ بوحدتها، أو null حين لا فرصةَ تكفي. */
  value: number | null;
  /** عددُ الفرص: تسليماتٌ مؤرَّخة، أو مراجعات، أو إجابات. */
  n: number;
  /** هل احتُسب في الدرجة. */
  counted: boolean;
  /** النقاطُ من مئة، أو null. */
  score: number | null;
  /** سببُ عدم الاحتساب — يُقال ولا يُترك فراغًا. */
  reason: string | null;
}

/**
 * حالةُ القياس لعضوٍ في الفريق.
 *
 * ولا يُقحَم صفرٌ مكان الغياب في أيّ طبقة: من لا حسابَ له لا درجةَ له
 * ولا صفر — والصفرُ لا يُكتب إلّا حين وُجدت فرصةٌ فلم تُستغلّ.
 */
export type KpiState = 'MEASURED' | 'NO_ACCOUNT' | 'NOT_MEASURED' | 'THIN_DATA' | 'NEW_HIRE';

export interface AgencyKpiCard {
  employeeId: string;
  name: string;
  jobTitle: string | null;
  deptKey: string | null;
  deptLabel: string | null;
  avatarUrl: string | null;
  userId: string | null;
  state: KpiState;
  stateReason: string | null;
  /** النقاطُ العامّة، أو null. */
  score: number | null;
  grade: KpiGrade | null;
  /** فرقُ النقاط عن النافذة السابقة غير المتداخلة. */
  trend: number | null;
  kpis: KpiValue[];
  /** ما يُقاس ولا يُحتسب — سياقٌ يقرؤه المدير. */
  context: {
    completed: number;
    assigned: number;
    openOverdue: number;
    undated: number;
    reviewed: number;
    /** التسليمُ في موعده مقيسًا على الموعد الحاليّ — الفجوةُ عن المجمَّد إشارة. */
    onTimeCurrent: number | null;
  };
}
